<template>
	<button
		v-if="isAdmin"
		type="button"
		class="pin-circle"
		:class="{ 'is-pinned': pinned }"
		:aria-pressed="pinned"
		:title="pinned ? t('unpin') : t('pin')"
		@click.stop="pinnedStore.togglePin(kind, id)">
		<Pin :size="14" class="pin-icon" />
	</button>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import { Pin } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'
	import { useAuthStore } from '@/stores/auth.store'
	import { usePinnedStore, type PinKind } from '@/stores/pinned.store'

	interface Props {
		kind: PinKind
		id: string
	}
	const props = defineProps<Props>()

	const { t } = useI18n()
	const authStore = useAuthStore()
	const pinnedStore = usePinnedStore()
	const pinned = computed(() => pinnedStore.isPinned(props.kind, props.id))
	// Pins sind Admins/Devs vorbehalten — zentral hier gegated, damit alle
	// Einbindungen (Projekt-/Standort-Header und -Listen) abgedeckt sind.
	const isAdmin = computed(() => ['admin', 'developer'].includes(authStore.role || ''))
</script>

<style scoped>
	@reference '@/styles/tailwind.css';

	.pin-circle {
		@apply inline-flex items-center justify-center;
		@apply h-6 w-6 rounded-full leading-none align-middle;
		@apply text-base-content/30;
		@apply transition;
		@apply hover:bg-warning/10 hover:text-warning/80;
	}
	.pin-circle .pin-icon {
		display: block;
		/* The lucide Pin glyph is visually top-heavy (head up, tail down).
		   Geometric center looks too high — nudge down for optical balance. */
		transform: translateY(1px);
	}
	.pin-circle.is-pinned {
		@apply text-warning;
	}
	.pin-circle.is-pinned .pin-icon {
		@apply fill-current;
	}
</style>
